import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { MdSearch, MdLocationCity, MdClose } from "react-icons/md";
import { Link } from "react-router-dom";

const routeStates = [
  "Andaman Nicobar",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chattisgarh",
  "Dadra Nagar Daman Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const CollegeList = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const [data, setData] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 100,
    totalPages: 0,
  });

  // View modal state
  const [showModal, setShowModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewCollege, setViewCollege] = useState(null);
  const [viewError, setViewError] = useState("");

  const limit = 100;

const [featuredLoading, setFeaturedLoading] = useState({});

  const fetchColleges = async () => {
    try {
      setLoading(true);

      const params = { page, limit };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (selectedState) params.state = selectedState.value;

      const res = await axios.get(`${base_url}/api/v1/colleges/colleges-new`, {
        params,
      });

      if (res.data.success) {
        setData(res.data.data || []);
        setPagination(
          res.data.pagination || {
            total: 0,
            page: 1,
            limit: 100,
            totalPages: 0,
          },
        );
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Fetch Colleges Error:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchColleges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedState, searchTerm]);
  
const toggleFeatured = async (id, currentValue) => {
  setFeaturedLoading((p) => ({ ...p, [id]: true }));
  // Optimistic update
  setData((prev) =>
    prev.map((c) =>
      c.id === id ? { ...c, is_featured: !currentValue } : c
    )
  );

  try {
    await axios.patch(
      `${base_url}/api/v1/colleges/${id}/featured`,
      { is_featured: !currentValue }
    );
  } catch (err) {
    console.error('Toggle featured failed:', err);
    // Revert on error
    setData((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, is_featured: currentValue } : c
      )
    );
    alert(err?.response?.data?.message || 'Failed to update featured status');
  } finally {
    setFeaturedLoading((p) => ({ ...p, [id]: false }));
  }
};

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleStateChange = (value) => {
    setSelectedState(value);
    setPage(1);
  };

  const clearState = () => {
    setSelectedState(null);
    setPage(1);
  };

  /*
   * View College Detail - matches GET /api/v1/colleges/new/:id
   * Returns { data: { courses: [...], cutoffRanks: [...] , ...collegeFields } }
   */
  const openViewModal = async (id) => {
    setShowModal(true);
    setViewLoading(true);
    setViewError("");
    setViewCollege(null);

    try {
      const res = await axios.get(`${base_url}/api/v1/colleges/new/${id}`);
      if (res.data.success) {
        setViewCollege(res.data.data);
      } else {
        setViewError(res.data.message || "Failed to load college details");
      }
    } catch (error) {
      console.error("Fetch College Detail Error:", error);
      setViewError(
        error?.response?.data?.message || "Failed to load college details",
      );
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setShowModal(false);
    setViewCollege(null);
    setViewError("");
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          {/* Header */}
          <div className="card-header bg-white">
            <div className="row align-items-center g-3">
              <div className="col-md-3">
                <h4 className="fw-bold mb-0">
                  <MdLocationCity className="me-2 text-primary" />
                  Colleges
                </h4>
              </div>

              <div className="col-md-4">
                <div className="input-group">
                  <span className="input-group-text">
                    <MdSearch />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search College Name or Code..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>

              <div className="col-md-5">
                <Select
                  options={routeStates.map((state) => ({
                    value: state,
                    label: state,
                  }))}
                  value={selectedState}
                  onChange={handleStateChange}
                  isClearable
                  placeholder="Select State"
                />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="card-body">
            {/* Filters */}
            <div className="mb-3 d-flex flex-wrap gap-2">
              <button
                className={`btn btn-sm ${
                  !selectedState ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={clearState}
              >
                All ({pagination.total})
              </button>

              {selectedState && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={clearState}
                >
                  Clear State
                </button>
              )}
            </div>

            {/* Table */}
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>College</th>
                    <th>Code</th>
                    <th>Management</th>
                    <th>University</th>
                    <th>State</th>
                    <th>City</th>
                    <th>Established</th>
                    <th>Intake</th>
                    <th>NMC Status</th>
                    <th>Courses</th>
                    <th style={{ width: "90px" }} className="text-center">Featured</th>
                    <th>Cutoffs</th>

                    <th>Website</th>
                    <th style={{ width: "140px" }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(10)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(13)].map((__, j) => (
                          <td key={j}>
                            <Skeleton />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : data.length > 0 ? (
                    data.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(page - 1) * limit + index + 1}</td>

                        {/* College */}
                        <td>
                          <div className="d-flex align-items-center">
                            {item.img1 && (
                              <img
                                src={item.img1}
                                alt=""
                                className="rounded-circle me-2"
                                width="40"
                                height="40"
                                style={{ objectFit: "cover" }}
                              />
                            )}
                            <div>
                              <strong>{item.college_name || "-"}</strong>
                              {item.status && (
                                <div className="small text-muted text-capitalize">
                                  {item.status}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Code */}
                        <td>
                          <span className="badge bg-secondary">
                            {item.college_code || "-"}
                          </span>
                        </td>

                        {/* Management */}
                        <td>{item.management_text || "-"}</td>

                        {/* University */}
                        <td>{item.university || "-"}</td>

                        {/* State */}
                        <td>
                          {item.state ? (
                            <span className="badge bg-primary">
                              {item.state}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* City */}
                        <td>{item.city || "-"}</td>

                        {/* Established */}
                        <td>{item.year_of_inc || "-"}</td>

                        {/* Intake */}
                        <td>{item.mbbs_intake ?? "-"}</td>

                        {/* NMC Status */}
                        <td>{item.nmc_status || "-"}</td>

                        {/* Course count */}
                        <td className="text-center">
                          <span className="badge bg-info text-light">
                            {item.course_count ?? 0}
                          </span>
                        </td>
<td className="text-center">
  <div className="form-check form-switch d-flex justify-content-center m-0">
    <input
      className="form-check-input"
      type="checkbox"
      role="switch"
      checked={!!item.is_featured}
      disabled={featuredLoading[item.id]}
      onChange={() => toggleFeatured(item.id, !!item.is_featured)}
      style={{ cursor: 'pointer', width: '42px', height: '22px' }}
      title={item.is_featured ? 'Remove from featured' : 'Mark as featured on home page'}
    />
  </div>
</td>

                        {/* Cutoff count */}
                        <td className="text-center">
                          <span className="badge bg-warning text-dark">
                            {item.cutoff_count ?? 0}
                          </span>
                        </td>

                        {/* Website */}
                        <td>
                          {item.website ? (
                            <a
                              href={
                                item.website.startsWith("http://") ||
                                item.website.startsWith("https://")
                                  ? item.website
                                  : `https://${item.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-primary"
                            >
                              Website
                            </a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openViewModal(item.id)}
                            >
                              View
                            </button>
                            <Link
                              to={`/dashboard/mbbs/edit-college/${item.id}`}
                            >
                              <button className="btn btn-sm btn-primary">
                                Edit
                              </button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="13" className="text-center py-5">
                        <div className="text-muted">
                          <MdLocationCity size={40} />
                          <div className="mt-2">No Colleges Found</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Showing {Math.min((page - 1) * limit + 1, pagination.total)}
                  {" - "}
                  {Math.min(page * limit, pagination.total)}
                  {" of "}
                  {pagination.total}
                  {" colleges"}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>

                  <span>
                    Page <strong>{page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong>
                  </span>

                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      {showModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeViewModal();
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {viewCollege?.college_name || "College Details"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeViewModal}
                >
                  <MdClose style={{ display: "none" }} />
                </button>
              </div>

              <div className="modal-body">
                {viewLoading && (
                  <div className="text-center py-4">
                    <Skeleton count={6} />
                  </div>
                )}

                {!viewLoading && viewError && (
                  <div className="alert alert-danger mb-0">{viewError}</div>
                )}

                {!viewLoading && !viewError && viewCollege && (
                  <>
                    {/* Basic details */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <strong>Code:</strong> {viewCollege.college_code || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Management:</strong>{" "}
                        {viewCollege.management_text || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>University:</strong>{" "}
                        {viewCollege.university || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>NMC Status:</strong>{" "}
                        {viewCollege.nmc_status || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Established:</strong>{" "}
                        {viewCollege.year_of_inc || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>MBBS Intake:</strong>{" "}
                        {viewCollege.mbbs_intake ?? "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>State:</strong> {viewCollege.state || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>City:</strong> {viewCollege.city || "-"}
                      </div>
                      <div className="col-md-12">
                        <strong>Address:</strong> {viewCollege.address || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Email:</strong> {viewCollege.email || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Telephone:</strong>{" "}
                        {viewCollege.telephone || "-"}
                      </div>
                      {viewCollege.description && (
                        <div className="col-md-12">
                          <strong>Description:</strong>
                          <p className="mb-0">{viewCollege.description}</p>
                        </div>
                      )}
                    </div>

                    {/* Courses */}
                    <h6 className="fw-bold">Courses</h6>
                    {viewCollege.courses && viewCollege.courses.length > 0 ? (
                      <div className="table-responsive mb-4">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Course</th>
                              <th>Intake</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewCollege.courses.map((c) => (
                              <tr key={c.id}>
                                <td>{c.course}</td>
                                <td>{c.intake ?? "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted mb-4">No courses added.</p>
                    )}

                    {/* Cutoff Ranks */}
                    <h6 className="fw-bold mb-3">Cutoff Ranks</h6>
                    {viewCollege.cutoffRanks &&
                    viewCollege.cutoffRanks.length > 0 ? (
                      viewCollege.cutoffRanks.map((cq, i) => {
                        // Collect all distinct round numbers used in this year/quota
                        // so we can render Category (rows) x Round (columns) as a
                        // pivot/matrix table -- much easier to scan than a flat list.
                        const roundSet = new Set();
                        cq.categories.forEach((cat) =>
                          cat.rounds.forEach((r) => roundSet.add(r.round)),
                        );
                        const rounds = [...roundSet].sort((a, b) => a - b);

                        // rankMap["CategoryName"][round] = rank
                        const rankMap = {};
                        cq.categories.forEach((cat) => {
                          rankMap[cat.name] = {};
                          cat.rounds.forEach((r) => {
                            rankMap[cat.name][r.round] = r.rank;
                          });
                        });

                        return (
                          <div key={i} className="card border mb-3">
                            <div className="card-header bg-light d-flex flex-wrap gap-2 align-items-center py-2">
                              <span className="badge bg-dark">
                                Year {cq.year}
                              </span>
                              {cq.quota && (
                                <span className="badge bg-secondary">
                                  {cq.quota}
                                </span>
                              )}
                            </div>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered mb-0 text-center align-middle">
                                <thead className="table-light">
                                  <tr>
                                    <th
                                      className="text-start"
                                      style={{ minWidth: "140px" }}
                                    >
                                      Category
                                    </th>
                                    {rounds.map((rnd) => (
                                      <th key={rnd}>Round {rnd}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {cq.categories.map((cat) => (
                                    <tr key={cat.name}>
                                      <td className="text-start fw-semibold">
                                        {cat.name}
                                      </td>
                                      {rounds.map((rnd) => {
                                        const rank = rankMap[cat.name]?.[rnd];
                                        return (
                                          <td key={rnd}>
                                            {rank !== undefined ? (
                                              <span className="badge bg-info text-light">
                                                {rank}
                                              </span>
                                            ) : (
                                              <span className="text-muted">
                                                -
                                              </span>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-muted mb-0">No cutoff ranks added.</p>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeViewModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeList;
